import { Cookie, Settings2, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useMarketingLocale } from "@/hooks/use-marketing-locale"

type Consent = {
  necessary: true
  preferences: boolean
  analytics: boolean
  marketing: boolean
  updatedAt: string
}

const storageKey = "resolto.cookie-consent.v1"
const defaultConsent: Consent = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
  updatedAt: "",
}

const readConsent = (): Consent | null => {
  if (typeof window === "undefined") {
    return null
  }

  try {
    const stored = window.localStorage.getItem(storageKey)

    return stored ? (JSON.parse(stored) as Consent) : null
  } catch {
    return null
  }
}

const applyConsent = (consent: Consent) => {
  document.documentElement.dataset.consentAnalytics = String(consent.analytics)
  document.documentElement.dataset.consentMarketing = String(consent.marketing)
  document.documentElement.dataset.consentPreferences = String(consent.preferences)
  window.dispatchEvent(new CustomEvent("resolto:consent-changed", { detail: consent }))
}

export function CookieConsent() {
  const { t } = useMarketingLocale()
  const [consent, setConsent] = useState<Consent>(() => readConsent() ?? defaultConsent)
  const [bannerVisible, setBannerVisible] = useState(() => readConsent() === null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const stored = readConsent()

    if (stored) {
      applyConsent(stored)
    }

    const open = () => dialogRef.current?.showModal()
    window.addEventListener("resolto:open-cookie-settings", open)

    return () => window.removeEventListener("resolto:open-cookie-settings", open)
  }, [])

  const persist = (next: Consent) => {
    const saved = {
      ...next,
      necessary: true as const,
      updatedAt: new Date().toISOString(),
    }
    window.localStorage.setItem(storageKey, JSON.stringify(saved))
    setConsent(saved)
    setBannerVisible(false)
    applyConsent(saved)
    dialogRef.current?.close()
  }

  const acceptAll = () =>
    persist({
      ...consent,
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
      updatedAt: "",
    })
  const rejectOptional = () => persist({ ...defaultConsent, updatedAt: "" })

  return (
    <>
      {bannerVisible && (
        <section
          className="cookie-banner"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-copy"
        >
          <div>
            <span className="cookie-banner__icon">
              <Cookie aria-hidden="true" />
            </span>
            <div>
              <h2 id="cookie-banner-title">{t("cookie.title")}</h2>
              <p id="cookie-banner-copy">{t("cookie.body")}</p>
              <a href="/legal/privacy">{t("cookie.policy")}</a>
            </div>
          </div>
          <div className="cookie-banner__actions">
            <button type="button" onClick={rejectOptional}>
              {t("cookie.reject")}
            </button>
            <button type="button" onClick={() => dialogRef.current?.showModal()}>
              <Settings2 aria-hidden="true" />
              {t("cookie.settings")}
            </button>
            <button type="button" className="is-primary" onClick={acceptAll}>
              {t("cookie.accept")}
            </button>
          </div>
        </section>
      )}

      <button
        type="button"
        className="cookie-settings-trigger"
        aria-label={t("cookie.open")}
        onClick={() => dialogRef.current?.showModal()}
      >
        <Cookie aria-hidden="true" />
      </button>

      <dialog
        ref={dialogRef}
        className="cookie-dialog"
        aria-labelledby="cookie-dialog-title"
        aria-describedby="cookie-dialog-intro"
        onClick={(event) => {
          if (event.target === dialogRef.current) {
            dialogRef.current.close()
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            dialogRef.current?.close()
          }
        }}
      >
        <div className="cookie-dialog__header">
          <div>
            <p>{t("cookie.controls")}</p>
            <h2 id="cookie-dialog-title">{t("cookie.dialogTitle")}</h2>
          </div>
          <button
            type="button"
            aria-label={t("cookie.close")}
            onClick={() => dialogRef.current?.close()}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <p id="cookie-dialog-intro" className="cookie-dialog__intro">
          {t("cookie.intro")}
        </p>
        <div className="cookie-dialog__options">
          <ConsentRow
            id="necessary"
            title={t("cookie.necessary")}
            description={t("cookie.necessaryBody")}
            checked
            disabled
            onChange={() => undefined}
          />
          <ConsentRow
            id="preferences"
            title={t("cookie.preferences")}
            description={t("cookie.preferencesBody")}
            checked={consent.preferences}
            onChange={(preferences) =>
              setConsent((current) => ({
                ...current,
                preferences,
              }))
            }
          />
          <ConsentRow
            id="analytics"
            title={t("cookie.analytics")}
            description={t("cookie.analyticsBody")}
            checked={consent.analytics}
            onChange={(analytics) => setConsent((current) => ({ ...current, analytics }))}
          />
          <ConsentRow
            id="marketing"
            title={t("cookie.marketing")}
            description={t("cookie.marketingBody")}
            checked={consent.marketing}
            onChange={(marketing) => setConsent((current) => ({ ...current, marketing }))}
          />
        </div>
        <div className="cookie-dialog__actions">
          <button type="button" onClick={rejectOptional}>
            {t("cookie.reject")}
          </button>
          <button type="button" onClick={() => persist(consent)}>
            {t("cookie.save")}
          </button>
          <button type="button" className="is-primary" onClick={acceptAll}>
            {t("cookie.accept")}
          </button>
        </div>
      </dialog>
    </>
  )
}

function ConsentRow({
  id,
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: {
  id: string
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  const inputId = `consent-${id}`

  return (
    <div className="cookie-dialog__option">
      <div>
        <label htmlFor={inputId}>{title}</label>
        <p>{description}</p>
      </div>
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </div>
  )
}
